<template>
  <v-app>
    <Snackbar :SnackBarComponent="SnackBarComponent" />
    
    <!-- Minimalist App Bar -->
    <!-- <v-app-bar elevation="0" app color="transparent" height="80">
      <v-container class="d-flex align-center">
        <v-img 
          :src="require('@/assets/logo1.png')" 
          max-width="160"
          contain
          class="ml-2"
        ></v-img>
        <v-spacer></v-spacer>
        <div class="text-caption text-grey-darken-1 mr-4">
          Secure Login Portal
        </div>
      </v-container>
    </v-app-bar> -->

    <v-main class="bg-gradient">
      <v-container fluid class="h-100 pa-0">
        <v-row class="h-100 ma-0 justify-center align-center">
          <!-- Centered Login Card -->
          <v-col 
            cols="12" 
            sm="10" 
            md="8" 
            lg="6" 
            xl="5"
            class="d-flex align-center justify-center pa-8"
          >
            <v-card 
              class="login-card elevation-6 rounded-xl"
              width="100%"
              max-width="520"
            >
              <v-card-text class="pa-8">
                <!-- Logo Header -->
                <div class="text-center mb-10">
                  <v-img
                    :src="require('@/assets/logo2.jpg')"
                    alt="Logo"
                    max-width="140"
                    max-height="50"
                    contain
                    class="mx-auto mb-6"
                  ></v-img>
                  <div class="text-h4 font-weight-bold primary--text mb-3">
                    {{ currentTitle }}
                  </div>
                  <div class="text-body-1 text-grey-darken-1">
                    {{ emailVerified ? 'Enter the OTP sent to your email' : 'Sign in to continue to your dashboard' }}
                  </div>
                </div>

                <!-- Progress Indicator -->
                <v-stepper 
                  v-model="step" 
                  alt-labels 
                  flat
                  class="transparent mb-8"
                >
                  <v-stepper-header class="transparent">
                    <v-stepper-item
                      :title="'Email'"
                      :complete="step > 1"
                      :value="1"
                    ></v-stepper-item>
                    <v-divider></v-divider>
                    <v-stepper-item
                      :title="'OTP'"
                      :value="2"
                    ></v-stepper-item>
                  </v-stepper-header>
                </v-stepper>

                <!-- Email Section -->
                <v-slide-x-transition>
                  <div v-if="!emailVerified">
                    <div class="text-subtitle-1 font-weight-medium mb-4">
                      <v-icon small color="primary" class="mr-2">mdi-email-outline</v-icon>
                      Enter your email address
                    </div>
                    <v-form ref="form" lazy-validation @submit.prevent="ValidateSignInForm">
                      <v-text-field
                        variant="outlined"
                        density="comfortable"
                        :rules="emailRules"
                        v-model="Login.EmailID"
                        placeholder="name@company.com"
                        type="email"
                        class="mb-6"
                        hide-details="auto"
                        height="56"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="primary" size="large">mdi-account-outline</v-icon>
                        </template>
                      </v-text-field>
                      
                      <v-btn
                        block
                        size="small"
                        :loading="loadingEmail"
                        color="primary"
                        variant="flat"
                        type="submit"
                        class="rounded-lg font-weight-bold"
                        :disabled="!Login.EmailID"
                        height="56"
                      >
                        Send OTP
                        <v-icon right>mdi-arrow-right</v-icon>
                      </v-btn>
                    </v-form>

                    <v-divider class="my-8"></v-divider>
                    
                    <div class="text-center">
                      <div class="text-body-2 text-grey-darken-1 mb-3">
                        Need help signing in?
                      </div>
                      <v-btn
                        variant="text"
                        color="primary"
                        size="small"
                        @click="showHelp = true"
                      >
                        Contact Support
                      </v-btn>
                    </div>
                  </div>
                </v-slide-x-transition>

                <!-- OTP Section -->
                <v-slide-x-transition>
                  <div v-if="emailVerified">
                    <!-- Email Display -->
                    <div class="verified-email-card pa-4 rounded-lg mb-8">
                      <div class="d-flex align-center justify-space-between">
                        <div class="d-flex align-center">
                          <v-icon color="success" class="mr-3" size="large">mdi-check-circle</v-icon>
                          <div>
                            <div class="text-caption text-grey-darken-1">Verification email sent to</div>
                            <div class="text-subtitle-1 font-weight-bold">{{ Login.EmailID }}</div>
                          </div>
                        </div>
                        <v-btn
                          size="small"
                          variant="text"
                          color="primary"
                          @click="resetEmail"
                          class="ml-2"
                        >
                          Change
                        </v-btn>
                      </div>
                    </div>

                    <!-- OTP Form -->
                    <div class="mb-6">
                      <div class="text-subtitle-1 font-weight-medium mb-4">
                        <v-icon small color="primary" class="mr-2">mdi-lock-outline</v-icon>
                        Enter OTP verification code
                      </div>
                      <v-form ref="formOTP" lazy-validation @submit.prevent="ValidateConfirmSignIn">
                        <div class="text-center mb-6">
                          <v-otp-input
                            length="6"
                            type="number"
                            variant="outlined"
                            density="comfortable"
                            v-model="Login.Password"
                            :disabled="loadingPassword"
                            class="otp-input-group"
                            @finish="ValidateConfirmSignIn"
                          ></v-otp-input>
                          <div class="text-body-2 text-grey-darken-1 mt-3">
                            Enter the 6-digit code sent to your email
                          </div>
                        </div>

                        <!-- Resend OTP Timer -->
                        <div class="text-center mb-8">
                          <v-btn
                            variant="text"
                            color="primary"
                            :disabled="resendTimer > 0"
                            @click="resendOTP"
                            size="small"
                          >
                            <template v-if="resendTimer > 0">
                              Resend OTP in {{ resendTimer }}s
                            </template>
                            <template v-else>
                              Resend OTP
                            </template>
                          </v-btn>
                        </div>

                        <v-btn
                          block
                          size="small"
                          variant="flat"
                          :loading="loadingPassword"
                          color="primary"
                          type="submit"
                          class="rounded-lg font-weight-bold mb-6"
                          height="56"
                        >
                          Verify & Continue
                          <v-icon right>mdi-login</v-icon>
                        </v-btn>
                      </v-form>
                    </div>

                    <!-- Back Button -->
                    <div class="text-center">
                      <v-btn
                        variant="text"
                        color="grey-darken-2"
                        @click="resetEmail"
                        size="small"
                      >
                        <v-icon left>mdi-arrow-left</v-icon>
                        Back to email
                      </v-btn>
                    </div>
                  </div>
                </v-slide-x-transition>

                <!-- Security Notice -->
                <!-- <div class="security-notice mt-10 pa-4 rounded-lg">
                  <div class="d-flex align-center justify-center">
                    <v-icon color="grey-darken-1" class="mr-3">mdi-shield-lock-outline</v-icon>
                    <div class="text-body-2 text-grey-darken-1">
                      Your information is secured with 256-bit SSL encryption
                    </div>
                  </div>
                </div> -->
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import { confirmSignIn, signIn, signOut } from "aws-amplify/auth";
import Snackbar from "@/components/Extras/Snackbar.vue";

export default {
  components: {
    Snackbar,
  },
  data: () => ({
    emailVerified: false,
    OTPEmail: "",
    ConfirmOTP: "",
    isPwdNew: false,
    loadingEmail: false,
    loadingPassword: false,
    step: 1,
    resendTimer: 30,
    showHelp: false,
    Password: {
      NewPassword: "",
      ConfirmPassword: "",
    },
    SnackBarComponent: {},
    Login: {
      EmailID: "",
      Password: "",
    },
    emailRules: [
      v => !!v || 'Email is required',
      v => /.+@.+\..+/.test(v) || 'Enter a valid email address',
    ],
    user: {},
  }),
  watch: {
    emailVerified(val) {
      this.step = val ? 2 : 1;
      if (val) {
        this.startResendTimer();
      }
    }
  },
  computed: {
    currentTitle() {
      return this.emailVerified ? "Verify OTP" : "Welcome to BMS";
    },
  },
  mounted() {},
  methods: {
    startResendTimer() {
      this.resendTimer = 30;
      const timer = setInterval(() => {
        if (this.resendTimer > 0) {
          this.resendTimer--;
        } else {
          clearInterval(timer);
        }
      }, 1000);
    },
    async resendOTP() {
      if (this.resendTimer > 0) return;
      
      try {
        this.loadingEmail = true;
        await signOut(); // Sign out first
        await this.SignIn(); // Resend OTP
        this.startResendTimer();
      } catch (error) {
        console.error("Resend error:", error);
      } finally {
        this.loadingEmail = false;
      }
    },
    resetEmail() {
      this.emailVerified = false;
      this.Login.Password = "";
      this.user = {};
      this.step = 1;
    },
    async ValidateConfirmSignIn() {
      if (!this.Login.Password || this.Login.Password.length !== 6) {
        this.SnackBarComponent = {
          SnackbarVmodel: true,
          SnackbarColor: "red",
          Top: true,
          SnackbarText: "Please enter a valid 6-digit OTP",
        };
        return;
      }
      
      try {
        this.loadingPassword = true;
        const result = await confirmSignIn({
          challengeResponse: this.Login.Password
        });
        
        this.ActivateMethod();
        this.loadingPassword = false;
        
      } catch (error) {
        console.log("error :", error);
        this.SnackBarComponent = {
          SnackbarVmodel: true,
          SnackbarColor: "red",
          Top: true,
          SnackbarText: error.message || "Invalid OTP Entered",
        };
        this.loadingPassword = false;
      }
    },
    ActivateMethod() {
      this.SnackBarComponent = {
        SnackbarVmodel: true,
        SnackbarColor: "green",
        Top: true,
        SnackbarText: "Successfully Logged In!",
      };
      setTimeout(() => this.$router.push("/Organization"), 300);
    },
    async ValidateSignInForm() {
      const isValid = await this.$refs.form.validate();
      if (isValid.valid) {
        await this.SignIn();
      } else {
        this.SnackBarComponent = {
          SnackbarVmodel: true,
          SnackbarColor: "red",
          Top: true,
          SnackbarText: "Please enter a valid email address",
        };
      }
    },
    async SignIn() {
      try {
        this.loadingEmail = true;
        this.user = await signIn({
          username: this.Login.EmailID.toLowerCase().trim(),
          options: {
            authFlowType: "CUSTOM_WITHOUT_SRP",
          },
        });
        
        this.$store.commit("SET_USEREMAIL", this.Login.EmailID);
        
        if (
          this.user.nextStep.signInStep ===
          "CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE"
        ) {
          this.emailVerified = true;
          this.SnackBarComponent = {
            SnackbarVmodel: true,
            SnackbarColor: "green",
            Top: true,
            SnackbarText: "OTP sent successfully! Check your email.",
          };
        }
        this.loadingEmail = false;
      } catch (error) {
        if (error.message === "There is already a signed in user.") {
          await signOut({ global: true });
          this.SignIn();
        }
        // console.log("error :", error);
        // this.SnackBarComponent = {
        //   SnackbarVmodel: true,
        //   SnackbarColor: "red",
        //   Top: true,
        //   SnackbarText: error.message || "Unable to send OTP. Please try again.",
        // };
        this.loadingEmail = false;
      }
    },
  },
};
</script>

<style scoped>
.bg-gradient {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
}

.login-card {
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.98);
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1) !important;
}

.verified-email-card {
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  border: 1px solid #81c784;
}

.security-notice {
  background: rgba(66, 133, 244, 0.05);
  border: 1px solid rgba(66, 133, 244, 0.1);
}

.otp-input-group {
  gap: 16px;
}

.otp-input-group :deep(.v-field) {
  height: 64px;
  width: 64px;
  border-radius: 16px;
  font-size: 1.75rem;
  font-weight: 600;
}

.v-stepper-header {
  padding: 0 !important;
}

.v-stepper-item {
  padding: 8px 0 !important;
}

@media (max-width: 600px) {
  .login-card {
    margin: 16px;
    padding: 16px !important;
  }
  
  .otp-input-group :deep(.v-field) {
    height: 56px;
    width: 56px;
  }
}
</style>